// src\themes\default\components\ProductScene.tsx
'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'motion/react';
import { textGroupVariants, textItemVariants } from './animations/textReveal';

type ImageInfo = { url: string; description?: string };

type ProductSceneItem = {
    sceneImage?: ImageInfo;   // عکس محیطی (محصول داخل دکور)
    productImage?: ImageInfo; // عکس دوربری‌شده‌ی محصول
    title?: string;
    link?: string;
};

type ProductSceneData = {
    title?: string;
    description?: string;
    items?: ProductSceneItem[];
};

// ردیف‌های زیگزاگی: یک طرف عکس محیطی، طرف دیگه عکس محصول + عنوان.
// ردیف‌های زوج جای دو ستون رو عوض می‌کنن. توی موبایل همه‌چی زیر هم میاد
// (اول عکس محیطی، بعد محصول) تا ترتیب همه‌ی ردیف‌ها یکسان باشه.
export default function ProductScene({ data }: { data: ProductSceneData }) {
    const items = (data?.items || []).filter(item => item.sceneImage?.url || item.productImage?.url);

    if (items.length === 0) return null;

    return (
        <div className="col-span-12">
            {(data.title || data.description) && (
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.4 }}
                    variants={textGroupVariants}
                    className="mb-8 lg:mb-12 space-y-1.5 text-center"
                >
                    {data.title && (
                        <motion.h2
                            variants={textItemVariants}
                            className="text-xl lg:text-3xl font-light text-primary-900 tracking-tight"
                        >
                            {data.title}
                        </motion.h2>
                    )}
                    {data.description && (
                        <motion.p variants={textItemVariants} className="text-[13px] text-primary-500">
                            {data.description}
                        </motion.p>
                    )}
                </motion.div>
            )}

            <div className="flex flex-col gap-12 md:gap-20 lg:gap-28">
                {items.map((item, index) => {
                    const reversed = index % 2 === 1;

                    const product = (
                        <div className="group/product flex flex-col items-center text-center">
                            {item.productImage?.url && (
                                // عکس محصول با نسبت طبیعی خودش نمایش داده میشه (بدون کادر ثابت)
                                <Image
                                    src={item.productImage.url}
                                    alt={item.productImage.description || item.title || ''}
                                    width={740}
                                    height={400}
                                    sizes="(max-width: 768px) 80vw, 370px"
                                    className="w-full max-w-[280px] md:max-w-[370px] h-auto transition-transform duration-700 ease-out group-hover/product:scale-[1.04]"
                                />
                            )}
                            {item.title && (
                                <h3 className="mt-4 md:mt-6 text-sm font-light text-primary-600 group-hover/product:text-primary-900 transition-colors">
                                    {item.title}
                                </h3>
                            )}
                        </div>
                    );

                    return (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="grid grid-cols-1 md:grid-cols-2 items-center gap-6 md:gap-10 lg:gap-16"
                        >
                            {item.sceneImage?.url && (
                                <div
                                    className={`relative w-full aspect-[16/9] overflow-hidden bg-primary-100 ${reversed ? 'md:order-2' : ''}`}
                                >
                                    <Image
                                        src={item.sceneImage.url}
                                        alt={item.sceneImage.description || item.title || ''}
                                        fill
                                        sizes="(max-width: 768px) 100vw, 50vw"
                                        className="object-cover"
                                    />
                                </div>
                            )}

                            <div className={`px-4 md:px-0 ${reversed ? 'md:order-1' : ''}`}>
                                {item.link ? <Link href={item.link}>{product}</Link> : product}
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
}
